import type {
    NativeInvoiceFieldSettings,
    NativeInvoicePdfFieldSettings,
} from "../schemas/domains/invoice/native-invoice-field-settings.schema";

const LEGACY_TOTALS_FIELD_IDS = [
    "subtotal",
    "tax",
    "discount",
    "deposit",
    "totalDue",
] as const;

const LEGACY_ONLY_TOTALS_FIELD_IDS = ["subtotal", "totalDue"] as const;

const PER_LINE_COLUMN_FIELD_IDS = ["tax", "discount", "deposit"] as const;

// Fields that used to live in `details` before the Payment/Notes split
// (PRO-12178) and now live in `notes`.
const LEGACY_NOTES_IN_DETAILS_FIELD_IDS = ["memo", "terms"] as const;

function isLegacyTotalsFieldSettings(
    fieldSettings: NativeInvoicePdfFieldSettings,
): boolean {
    const hasTotals = Object.keys(fieldSettings.totals ?? {}).length > 0;
    return (
        !hasTotals &&
        LEGACY_ONLY_TOTALS_FIELD_IDS.some(
            (fieldId) => fieldSettings.itemsTable?.[fieldId] !== undefined,
        )
    );
}

// Snapshots saved before the Payment/Notes split (PRO-12178) stored
// memo/terms in `details` alongside invoiceNumber/poNumber/invoiceDate/
// dueDate, with no `notes` group at all. Moves those two fields to `notes`
// so an existing invoice renders exactly as it did before the split (the
// visibility/label settings carry over unchanged - only their group
// membership moves).
function isLegacyNotesInDetails(
    fieldSettings: NativeInvoicePdfFieldSettings,
): boolean {
    const hasNotes = Object.keys(fieldSettings.notes ?? {}).length > 0;
    return (
        !hasNotes &&
        LEGACY_NOTES_IN_DETAILS_FIELD_IDS.some(
            (fieldId) => fieldSettings.details?.[fieldId] !== undefined,
        )
    );
}

function migrateLegacyTotals(
    fieldSettings: NativeInvoicePdfFieldSettings,
): NativeInvoicePdfFieldSettings {
    if (!isLegacyTotalsFieldSettings(fieldSettings)) {
        return fieldSettings;
    }
    const legacyItemsTable = fieldSettings.itemsTable ?? {};
    const totals: NativeInvoiceFieldSettings = {};
    for (const fieldId of LEGACY_TOTALS_FIELD_IDS) {
        const setting = legacyItemsTable[fieldId];
        if (setting) {
            totals[fieldId] = setting;
        }
    }
    const itemsTable: NativeInvoiceFieldSettings = { ...legacyItemsTable };
    for (const fieldId of LEGACY_ONLY_TOTALS_FIELD_IDS) {
        delete itemsTable[fieldId];
    }
    for (const fieldId of PER_LINE_COLUMN_FIELD_IDS) {
        const setting = legacyItemsTable[fieldId];
        if (setting) {
            itemsTable[fieldId] = { ...setting, isIncludedOnPdf: true };
        }
    }
    return { ...fieldSettings, itemsTable, totals };
}

function migrateLegacyNotes(
    fieldSettings: NativeInvoicePdfFieldSettings,
): NativeInvoicePdfFieldSettings {
    if (!isLegacyNotesInDetails(fieldSettings)) {
        return fieldSettings;
    }
    const legacyDetails = fieldSettings.details ?? {};
    const notes: NativeInvoiceFieldSettings = {};
    const details: NativeInvoiceFieldSettings = { ...legacyDetails };
    for (const fieldId of LEGACY_NOTES_IN_DETAILS_FIELD_IDS) {
        const setting = legacyDetails[fieldId];
        if (setting) {
            notes[fieldId] = setting;
            delete details[fieldId];
        }
    }
    return { ...fieldSettings, details, notes };
}

export function normalizeNativeInvoicePdfFieldSettings(
    fieldSettings: NativeInvoicePdfFieldSettings,
): NativeInvoicePdfFieldSettings {
    return migrateLegacyNotes(migrateLegacyTotals(fieldSettings));
}
